import { useEffect, useState } from 'react';

import DownloadIcon from '@mui/icons-material/Download';
import { Button } from '@mui/material';
import { Link, useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { RouterParams } from 'containers/AppRouter';
import ButtonActionDelete from 'shared/components/Button/Action/ButtonActionDelete/ButtonActionDelete';
import ButtonActionList, { ButtonActionListT } from 'shared/components/Button/Action/ButtonActionList/ButtonActionList';
import Loader from 'shared/containers/Loader';
import SCHCTemplate from 'shared/models/SCHCTemplate';
import api from 'utils/api';
import { formatDateFilename } from 'utils/date';
import downloadFile, { FileInputType, FileOutputType } from 'utils/download';
import { hasPermissionOnSCHCTemplate, useAuth } from 'utils/hooks/providers/auth-context';
import { useEntityNameForNav } from 'utils/hooks/providers/entity-name-for-nav-context';

import './SCHCTemplatesDetails.scss';

import SCHCTemplatesDetailsRender from './components/SCHCTemplatesDetailsRender';

function SCHCTemplatesDetails() {
  const [isLoading, setIsLoading] = useState(false);
  const [dataDetails, setdataDetails] = useState<SCHCTemplate.API.BodyT>(
    {} as SCHCTemplate.API.BodyT,
  );
  const history = useHistory();
  const { schcTemplateId } = useParams() as RouterParams;
  const { authState } = useAuth();

  const { setEntityNameForNav } = useEntityNameForNav();

  const actions: Array<ButtonActionListT> = [
    {
      name: 'JSON',
      fn: () => {
        api.SCHCTemplates.downloadJSON(schcTemplateId as string)
          .then((resp: Response) => {
            if (resp.status >= 400) {
              toast.error(`Failed to download the templates as JSON - Error ${resp.status}`);
            } else {
              resp.json()
                .then((data: any) => {
                  try {
                    downloadFile.inBrowser(
                      FileInputType.JSONString,
                      FileOutputType.JSON,
                      data,
                      `template_${dataDetails.name}_${formatDateFilename()}.json`,
                    );
                  } catch {
                    toast.error('Failed to load in browser the received JSON file');
                  }
                });
            }
          });
      },
      icon: <DownloadIcon />,
    },
    {
      name: 'CBOR Package',
      fn: () => {
        api.SCHCTemplates.downloadCBORPackage(schcTemplateId as string)
          .then((resp: Response) => {
            if (resp.status >= 400) {
              toast.error(`Failed to download the templates as CBOR Package - Error ${resp.status}`);
            } else {
              resp.json()
                .then((data: any) => {
                  try {
                    downloadFile.inBrowser(
                      FileInputType.Base64,
                      FileOutputType.ZIP,
                      data.cborPackage,
                      data.filename,
                    );
                  } catch {
                    toast.error('Failed to load in browser the received archive file');
                  }
                });
            }
          });
      },
      icon: <DownloadIcon />,
    },
  ];

  const loadSourceData = () => {
    setIsLoading(true);
    api.SCHCTemplates.getOne(schcTemplateId as string)
      .then((resp: any) => {
        if (resp.status >= 400) {
          resp.json()
            .then(() => {
              toast.error(`Failed to load the SCHC Template with ID '${schcTemplateId}'`);
              history.push('/schc-templates');
            });
          setIsLoading(false);
        } else {
          resp.json()
            .then((data: SCHCTemplate.API.BodyT) => {
              setdataDetails(data);
              setEntityNameForNav(data.name);
              setIsLoading(false);
            });
        }
      });
  };

  useEffect(() => {
    loadSourceData();
  }, []);

  if (isLoading) {
    return <Loader.Page />;
  }
  return (
    <div>
      <div className="title__header">
        <h1 className="text-first-letter-uppercase">SCHC Template details</h1>
        <div className="title__header--actions">
          <ButtonActionList list={actions} />
          {
            hasPermissionOnSCHCTemplate(authState, dataDetails.organization) ? (
              <>
                <Link to={`/schc-templates/${schcTemplateId}/edit`}>
                  <Button variant="contained" color="secondary">Edit</Button>
                </Link>
                <ButtonActionDelete
                  targetId={schcTemplateId as string}
                  api={api.SCHCTemplates}
                  messageComplement={`this SCHC Template named '${dataDetails.name}'`}
                  redirectLocation="/schc-templates"
                />
              </>
            ) : null
          }
        </div>
      </div>
      <SCHCTemplatesDetailsRender data={dataDetails} />
    </div>
  );
}

export default SCHCTemplatesDetails;
